const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && Boolean(value.trim());

const unexpectedKey = (value: Record<string, unknown>, allowedKeys: readonly string[]): string | undefined =>
  Object.keys(value).find((key) => !allowedKeys.includes(key));

export const validateBundleRecipesConfig = (config: unknown): string | null => {
  if (!isRecord(config)) {
    return 'Config must be a JSON object like { "recipes": { ... } }';
  }
  const unexpectedConfigKey = unexpectedKey(config, ['recipes']);
  if (unexpectedConfigKey) {
    return `Config contains unsupported field "${unexpectedConfigKey}"`;
  }

  const recipes = config['recipes'];
  if (recipes === undefined) {
    return null;
  }
  if (!isRecord(recipes)) {
    return '"recipes" must be an object keyed by bundle SKU';
  }

  for (const [bundleSku, components] of Object.entries(recipes)) {
    if (!isNonEmptyString(bundleSku)) {
      return 'Every recipe needs a bundle SKU';
    }
    if (!Array.isArray(components) || components.length === 0) {
      return `Bundle ${bundleSku} needs at least one component`;
    }

    const seenComponentSkus = new Set<string>();
    for (const component of components as unknown[]) {
      if (!isRecord(component)) {
        return `Bundle ${bundleSku} has a component that is not an object`;
      }
      const unexpectedComponentKey = unexpectedKey(component, ['sku', 'quantity']);
      if (unexpectedComponentKey) {
        return `Bundle ${bundleSku} has a component with unsupported field "${unexpectedComponentKey}"`;
      }
      const { sku, quantity } = component;
      if (!isNonEmptyString(sku)) {
        return `Bundle ${bundleSku} has a component without a SKU`;
      }
      if (!Number.isSafeInteger(quantity) || (quantity as number) < 1) {
        return `Bundle ${bundleSku}: component ${sku} needs a whole quantity of at least 1`;
      }
      if (sku === bundleSku) {
        return `Bundle ${bundleSku} cannot list itself as a component`;
      }
      if (seenComponentSkus.has(sku)) {
        return `Bundle ${bundleSku} lists component ${sku} more than once`;
      }
      seenComponentSkus.add(sku);
    }
  }

  return null;
};
